// canvas/trick-area.js: center-of-table trick renderer (M2.c-4).
//
// Renders the four cards currently played to the trick area, every card
// UPRIGHT (T3, 2026-06 audit: rotated side cards made ranks unreadable,
// readability beats table metaphor). Attribution comes from position:
// each seat owns a fixed slot in a plus-shaped cross, and the slide-in
// animation from the seat's hand area reinforces who played what.
//
// When animation_state.trick_resolution is present, the cards sweep
// toward the winner's nameplate over 130ms with ease-out, the winning
// card scales 1.0 → 1.06 → 1.0, and the winner's nameplate border
// pulses in trump-color for 120ms from the start of the sweep (restyle
// step 2, as in the chosen mockup the pop runs during the sweep: 130ms
// total; was 300 + 300 in a row per synthesis §3 Pillar 3).
//
// Reduced-motion path collapses to a 150ms cross-fade, no sweep, no
// scale punch, per synthesis §2 prefers-reduced-motion contract.
//
// Per-seat trick piles (face-down stacks) sit next to each seat's
// nameplate, growing by one card per won trick. M2.c-6 will revisit
// pile placement when the scoreboard goes in.

import { renderCard, isTrumpCard, _internals as cardInternals } from './cards.js';
import { canvasFont } from './text.js';
import { trumpBadgeStackRect } from './trump-badge.js';
// M5 #2: canonical easing, replaces local easeOut. springOvershoot
// matches the synthesis §4.4 spring curve, used for the winning-card
// landing pulse so the moment reads as a spring "punch," not a sine
// puff.
import { easeOut as easingEaseOut, springOvershoot } from '../ui/easing.js';

const { SUIT_NAME } = cardInternals;

// Pure-canvas colors (kept local; if these multiply, hoist to a tokens module).
const COLOR_TRUMP_FLASH_H = '#E03048';   // hearts / diamonds, red
const COLOR_TRUMP_FLASH_D = '#E03048';
const COLOR_TRUMP_FLASH_S = '#1A2540';   // spades / clubs, dark navy
const COLOR_TRUMP_FLASH_C = '#1A2540';
const FLASH_COLORS = { H: COLOR_TRUMP_FLASH_H, D: COLOR_TRUMP_FLASH_D, S: COLOR_TRUMP_FLASH_S, C: COLOR_TRUMP_FLASH_C };

// Trick-card dimensions. Slightly smaller than a hand card to leave
// breathing room around 4 cards in the trick area.
const TRICK_CARD_W_DESKTOP = 60;
const TRICK_CARD_W_MOBILE  = 48;
const TRICK_CARD_ASPECT    = 3.5 / 2.5;

// Per-seat rotation in radians. T3: all upright, DM2-3's per-seat
// rotation is retired (rotated ranks were unreadable). The table stays
// because drawRotatedCard and the slide overlay read rotation from the
// slot, which keeps the plumbing live if a future layout wants tilt.
const SEAT_ROTATION = {
  1: 0,
  2: 0,
  3: 0,
  4: 0,
};

// Sweep / flash phase boundaries.
// Restyle step 2: the same sweep and pop as ui/trick-sequencer.js, which
// feeds this renderer the time since the sweep began.
const SWEEP_DURATION_MS         = 130;
const FLASH_DURATION_MS         = 120;  // starts with the sweep, ends inside it
const RESOLUTION_DURATION_MS    = Math.max(SWEEP_DURATION_MS, FLASH_DURATION_MS);  // 130
const REDUCED_DURATION_MS       = 150;
const WINNING_PULSE_PEAK_SCALE  = 1.06;

// Pile geometry: face-down mini cards stacked next to each seat's
// nameplate with a 2px offset per trick.
const PILE_CARD_W_DESKTOP = 28;
const PILE_CARD_W_MOBILE  = 22;
const PILE_STACK_OFFSET   = 2;

// Local re-export so the test suite that already imports `easeOut` from
// this module keeps working unchanged. The implementation now lives in
// ui/easing.js (M5 #2).
const easeOut = easingEaseOut;

// #12/T3: with every card upright the cross footprint is 3 cards wide
// (left + center column + right) by 2 cards tall (top and bottom clear
// each other; the side cards only share vertical range with them, never
// horizontal). Shrink the card from its desired size when needed so the
// whole cross fits inside the reserved trick area and never spills into
// the seat/hand bands on small viewports.
const TRICK_GAP = 6;
function cardSize(layout) {
  const desired = layout.isMobile ? TRICK_CARD_W_MOBILE : TRICK_CARD_W_DESKTOP;
  // -2 absorbs the round() applied to h and the offsets below.
  const fitW = Math.floor(Math.min(
    (layout.trickArea.w - 2 * TRICK_GAP - 2) / 3,
    (layout.trickArea.h - 2 * TRICK_GAP - 2) / (2 * TRICK_CARD_ASPECT),
  ));
  const w = Math.max(24, Math.min(desired, fitW)); // floor at 24px for legibility
  const h = Math.round(w * TRICK_CARD_ASPECT);
  return { w, h };
}

/**
 * Center-of-trick-area position for a card played by `seat`. Top-left
 * coords, accounting for the rotation offset so the rendered card stays
 * inside the trick area.
 */
function trickPosForSeat(seat, layout) {
  const { w, h } = cardSize(layout);
  const cx = layout.trickArea.x + Math.round(layout.trickArea.w / 2);
  const cy = layout.trickArea.y + Math.round(layout.trickArea.h / 2);
  // T3 upright geometry. Side cards must clear the center column
  // horizontally (their centers sit a full card-width out); top/bottom
  // cards only need to clear each other vertically (half a card-height
  // each side of center). The side cards overlap the top/bottom cards'
  // vertical range, but never their horizontal range, a tight plus.
  const hOffset = w + TRICK_GAP;
  const vOffset = Math.round(h / 2) + TRICK_GAP;
  // FOLLOWUPS #8: layout.seats is positional (bottom/left/top/right) but
  // its ids carry the viewer rotation, resolve the lua seat to its
  // physical slot so the trick card lands in front of the right plate.
  const slot = layout.seats.findIndex((s) => s.id === seat);
  switch (slot) {
    case 0: return { x: cx - Math.round(w / 2),           y: cy + vOffset - Math.round(h / 2), w, h, rotation: SEAT_ROTATION[1] }; // bottom
    case 1: return { x: cx - hOffset - Math.round(w / 2), y: cy - Math.round(h / 2),           w, h, rotation: SEAT_ROTATION[2] }; // left
    case 2: return { x: cx - Math.round(w / 2),           y: cy - vOffset - Math.round(h / 2), w, h, rotation: SEAT_ROTATION[3] }; // top
    case 3: return { x: cx + hOffset - Math.round(w / 2), y: cy - Math.round(h / 2),           w, h, rotation: SEAT_ROTATION[4] }; // right
    default: return { x: cx, y: cy, w, h, rotation: 0 };
  }
}

/**
 * B4b-1 U1: a card in flight (slide in, sweep out) that would lie on the
 * score box or the trump badge (with its label) moves sideways off it,
 * to the side that keeps it on the canvas with the shorter move. On
 * portrait phones the side players' fans and plates sit straight above
 * the score box and the pinned badge, so a straight path from them
 * crossed "US 0" (375x667) and the badge. Cards at rest never touch
 * either.
 *
 * @param {{x:number, y:number, w:number, h:number}} card
 * @param {import('./layout.js').TableLayout} layout
 * @param {any} state  snapshot (where the desktop badge parks)
 */
function clearOfTableMarks(card, layout, state) {
  const boxes = [layout.scoreboard];
  const badge = trumpBadgeStackRect(layout, state ?? {});
  if (badge) boxes.push(badge);
  const gap = 4;
  let out = card;
  // Two passes: a move off one box may not land on the other.
  for (let pass = 0; pass < 2; pass += 1) {
    for (const sb of boxes) {
      const hits = out.x < sb.x + sb.w + gap && sb.x - gap < out.x + out.w
        && out.y < sb.y + sb.h + gap && sb.y - gap < out.y + out.h;
      if (!hits) continue;
      const leftX = sb.x - gap - out.w;
      const rightX = sb.x + sb.w + gap;
      const fitsLeft = leftX >= 0;
      const fitsRight = rightX + out.w <= layout.canvasW;
      let x;
      if (fitsLeft && fitsRight) x = out.x - leftX <= rightX - out.x ? leftX : rightX;
      else x = fitsRight && !fitsLeft ? rightX : leftX;
      out = { ...out, x };
    }
  }
  return out;
}

/**
 * Center coord of a seat's nameplate, sweep target.
 */
function nameplateCenter(seat, layout) {
  const s = layout.seats.find((sb) => sb.id === seat);
  if (!s) return { x: layout.trickArea.x, y: layout.trickArea.y };
  return { x: s.centerX, y: s.centerY };
}

/**
 * Per-seat trick-pile position, to the OUTSIDE of each seat's nameplate
 * (away from the trick area) so it doesn't collide with cards in hand or
 * trick area. M2.c-6 may reshuffle this.
 */
function pilePosForSeat(seat, layout) {
  const s = layout.seats.find((sb) => sb.id === seat);
  if (!s) return null;
  const w = layout.isMobile ? PILE_CARD_W_MOBILE : PILE_CARD_W_DESKTOP;
  const h = Math.round(w * TRICK_CARD_ASPECT);
  const gap = 6;
  // Place on the OPPOSITE side of the seat from the trick area, so the
  // pile reads as "theirs" not "in play".
  if (s.align === 'bottom') return { x: s.x + s.w + gap, y: s.y + Math.round((s.h - h) / 2), w, h };
  if (s.align === 'top')    return { x: s.x - w - gap,   y: s.y + Math.round((s.h - h) / 2), w, h };
  if (s.align === 'left')   return { x: s.x + Math.round((s.w - w) / 2), y: s.y - h - gap,   w, h };
  return                        { x: s.x + Math.round((s.w - w) / 2), y: s.y + s.h + gap, w, h };
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {{
 *   trick_plays?: Array<{seat:number, card_id:string}>,
 *   trick_counts_per_seat?: Record<number, number>,
 *   trump_suit?: string|null,
 * }} state
 * @param {import('./layout.js').TableLayout} layout
 * @param {{
 *   trick_resolution?: {
 *     winner_seat: number,
 *     elapsed_ms: number,
 *     reduced_motion?: boolean,
 *   } | null,
 *   slide?: {
 *     seat: 1|2|3|4,
 *     card_id: string,
 *     t: number,
 *   } | null,
 * }} [animation]
 * @returns {Array<{card_id:string, seat:number, x:number, y:number, w:number, h:number, trump:boolean, resting:boolean}>}
 *   the trick cards drawn this frame, in CSS px (resting = upright, full
 *   size and opacity, not sliding or sweeping). The views publish this
 *   list on the canvas (data-drawn-cards) so browser tests can find the
 *   cards.
 */
export function renderTrickArea(ctx, state, layout, animation = {}) {
  /** @type {Array<{card_id:string, seat:number, x:number, y:number, w:number, h:number, trump:boolean, resting:boolean}>} */
  const drawn = [];
  const plays = state.trick_plays || [];
  const resolution = animation.trick_resolution || null;
  const slide = animation.slide || null;
  const reducedMotion = !!(resolution && resolution.reduced_motion);

  // Resolution duration depends on reduced-motion preference.
  const totalDur = reducedMotion ? REDUCED_DURATION_MS : RESOLUTION_DURATION_MS;

  // Sweep progress 0..1 over SWEEP_DURATION_MS (skipped in reduced motion).
  let sweepT = 0;
  if (resolution && !reducedMotion) {
    sweepT = Math.min(1, resolution.elapsed_ms / SWEEP_DURATION_MS);
  }

  // Slide overlay: when a card is mid-flight from its seat's hand area
  // to the trick slot, suppress the static-slot draw for that seat
  // (the slide draws it instead at the interpolated position). Trick
  // resolution always wins over slide, once the trick resolves the
  // cards belong to the winner sweep, not still-in-flight.
  const slideActiveForSeat = (seatId) => (
    !!slide && !resolution && slide.seat === seatId
  );


  // 1. Draw the played cards (or their swept positions).
  for (const play of plays) {
    if (slideActiveForSeat(play.seat)) continue;  // drawn below as slide overlay
    const start = trickPosForSeat(play.seat, layout);
    let { x, y, w, h, rotation } = start;
    let opacity = 1;
    let scale = 1;

    if (resolution) {
      if (reducedMotion) {
        // Cross-fade out over the full 150ms.
        opacity = 1 - Math.min(1, resolution.elapsed_ms / totalDur);
      } else {
        // Sweep target = winner nameplate. Cards arc from start to target.
        const target = nameplateCenter(resolution.winner_seat, layout);
        const eased = easeOut(sweepT);
        x = Math.round(start.x + (target.x - (start.x + w / 2)) * eased);
        y = Math.round(start.y + (target.y - (start.y + h / 2)) * eased);
        ({ x, y } = clearOfTableMarks({ x, y, w, h }, layout, state));
        // The cards fade out over the second half of the sweep, so they
        // are gone when it ends (the sequence then drops them).
        opacity = Math.min(1, 2 * (1 - sweepT));
        // Winning card scale pulse (1.0 → 1.06 → 1.0 over the sweep).
        if (play.seat === resolution.winner_seat) {
          const t = sweepT;
          // sine bump: 0 at t=0 and t=1, peak at t=0.5
          const pulse = Math.sin(t * Math.PI);
          scale = 1 + (WINNING_PULSE_PEAK_SCALE - 1) * pulse;
        }
      }
    }

    const trump = isTrumpCard(play.card_id, state.trump_suit);
    drawRotatedCard(ctx, play.card_id, x, y, w, h, rotation, opacity, scale, trump);
    drawn.push({
      card_id: play.card_id, seat: play.seat, x, y, w, h, trump,
      resting: !rotation && scale === 1 && opacity === 1 && !resolution,
    });
  }

  // 1b. Slide overlay: draw the in-flight card at its interpolated
  // position. The card is omitted from step 1 above for `slide.seat`,
  // so this is its only on-screen draw during the slide window.
  if (slide && !resolution) {
    const r = drawSlideCard(ctx, slide, layout, state);
    drawn.push({ card_id: slide.card_id, seat: slide.seat, ...r, resting: false });
  }
  // B4b-1 e2e seam: "x,y,w,h" of every trick card drawn this frame, so a
  // browser test reads the drawn cards (slides and sweeps included).
  // Restyle step 2: data-trick-landed counts the cards at rest in their
  // slots (slid in, not yet sweeping), so a pace test can time landings.
  const el = ctx.canvas;
  if (el && el.dataset) {
    if (drawn.length) {
      el.dataset.trickCards = drawn.map((d) => `${d.x},${d.y},${d.w},${d.h}`).join(';');
      el.dataset.trickLanded = String(drawn.filter((d) => d.resting).length);
    } else {
      delete el.dataset.trickCards;
      delete el.dataset.trickLanded;
    }
  }

  // 2. Winner-nameplate trump-color flash overlay, during the sweep.
  if (resolution && !reducedMotion) {
    const flashT = flashProgress(resolution.elapsed_ms);
    if (flashT > 0 && flashT < 1) drawWinnerFlash(ctx, resolution.winner_seat, layout, state.trump_suit, flashT);
  }

  // 3. Per-seat trick piles.
  const counts = state.trick_counts_per_seat || {};
  for (const seat of layout.seats) {
    const n = counts[seat.id] || 0;
    if (n <= 0) continue;
    drawPile(ctx, seat.id, n, layout);
  }
  return drawn;
}

/**
 * Pure helper: pick a sensible "from" point for the slide overlay.
 * Bot hands are rendered as small fans/stacks around the seat's
 * nameplate; using handArea center reads as "the card lifted off
 * from here." For the player (seat 1) the hand sits below, so the
 * card still arcs upward into the trick area.
 *
 * Exported via _internals for unit testing, keeps the geometry
 * pure and lets the spec drive new layouts without canvas mocks.
 */
function slideFromCenter(seatId, layout) {
  const s = layout.seats.find((sb) => sb.id === seatId);
  if (!s || !s.handArea) return { x: s?.centerX ?? 0, y: s?.centerY ?? 0 };
  const ha = s.handArea;
  return {
    x: ha.x + Math.round(ha.w / 2),
    y: ha.y + Math.round(ha.h / 2),
  };
}

function drawSlideCard(ctx, slide, layout, state) {
  const end = trickPosForSeat(slide.seat, layout);
  const start = slideFromCenter(slide.seat, layout);
  const t = Math.max(0, Math.min(1, slide.t));
  // Interpolate the card's *center* so rotation around center is stable.
  const endCx = end.x + end.w / 2;
  const endCy = end.y + end.h / 2;
  const cx = start.x + (endCx - start.x) * t;
  const cy = start.y + (endCy - start.y) * t;
  const { x, y } = clearOfTableMarks(
    { x: Math.round(cx - end.w / 2), y: Math.round(cy - end.h / 2), w: end.w, h: end.h }, layout, state);
  const trump = isTrumpCard(slide.card_id, state.trump_suit);
  drawRotatedCard(ctx, slide.card_id, x, y, end.w, end.h, end.rotation, 1, 1, trump);
  return { x, y, w: end.w, h: end.h, trump };
}

function drawRotatedCard(ctx, cardId, x, y, w, h, rotation, opacity, scale, trump = false) {
  ctx.save();
  if (opacity < 1) ctx.globalAlpha = Math.max(0, opacity);
  // Upright and unscaled (every resting trick card): draw at the card's
  // own whole-pixel box. Translating to the centre first put odd-sized
  // cards on a half pixel (cx = x + w/2), which blurred the pixel art.
  if (!rotation && scale === 1) {
    renderCard(ctx, cardId, x, y, w, h, { face_up: true, trump });
    ctx.restore();
    return;
  }
  // Rotate + scale around card center.
  const cx = x + w / 2;
  const cy = y + h / 2;
  ctx.translate(cx, cy);
  if (rotation) ctx.rotate(rotation);
  if (scale !== 1) ctx.scale(scale, scale);
  renderCard(ctx, cardId, -w / 2, -h / 2, w, h, { face_up: true, trump });
  ctx.restore();
}

function drawWinnerFlash(ctx, seatId, layout, trumpSuit, flashT) {
  const s = layout.seats.find((sb) => sb.id === seatId);
  if (!s) return;
  // Use a sine pulse so the flash builds and decays smoothly within the
  // 120ms flash window. Trump color falls back to gold if unknown.
  const color = (trumpSuit && FLASH_COLORS[trumpSuit]) || '#F0C040';
  const alpha = Math.sin(flashT * Math.PI) * 0.95;
  if (alpha <= 0) return;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = color;
  ctx.lineWidth = 4;
  ctx.shadowColor = color;
  ctx.shadowBlur = 14;
  const r = 10;
  ctx.beginPath();
  ctx.moveTo(s.x + r, s.y);
  ctx.arcTo(s.x + s.w, s.y,        s.x + s.w, s.y + s.h, r);
  ctx.arcTo(s.x + s.w, s.y + s.h,  s.x,       s.y + s.h, r);
  ctx.arcTo(s.x,       s.y + s.h,  s.x,       s.y,       r);
  ctx.arcTo(s.x,       s.y,        s.x + s.w, s.y,       r);
  ctx.closePath();
  ctx.stroke();
  ctx.restore();
}

function drawPile(ctx, seatId, count, layout) {
  const base = pilePosForSeat(seatId, layout);
  if (!base) return;
  // Stack `count` mini face-down cards with PILE_STACK_OFFSET per card.
  for (let i = 0; i < count; i++) {
    renderCard(ctx, '', base.x + i * PILE_STACK_OFFSET, base.y + i * PILE_STACK_OFFSET, base.w, base.h, { face_up: false });
  }
  // Counter badge below the stack.
  ctx.save();
  ctx.fillStyle = '#F0C040';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = canvasFont(11, { weight: 600 });
  const cx = base.x + Math.round(base.w / 2) + (count - 1) * PILE_STACK_OFFSET / 2;
  const cy = base.y + base.h + (count - 1) * PILE_STACK_OFFSET + 10;
  ctx.fillText(`×${count}`, cx, cy);
  ctx.restore();
}

/**
 * Pure helper: the winner flash's progress 0..1 for the time since the
 * sweep began. The flash starts with the sweep (the mockup's pop).
 * @param {number} elapsedMs
 */
function flashProgress(elapsedMs) {
  return Math.max(0, Math.min(1, elapsedMs / FLASH_DURATION_MS));
}

/**
 * Pure helper: did the resolution animation finish? Used by the rAF hook.
 */
export function isResolutionComplete(elapsed_ms, reduced_motion) {
  const total = reduced_motion ? REDUCED_DURATION_MS : RESOLUTION_DURATION_MS;
  return elapsed_ms >= total;
}

/**
 * Pure helper: human-readable winner announcement for aria-live.
 */
export function describeTrickWinner(winnerSeat, seatLabels) {
  const label = seatLabels?.[winnerSeat] || `Seat ${winnerSeat}`;
  return `${label} wins the trick.`;
}

export const _internals = {
  SEAT_ROTATION,
  SWEEP_DURATION_MS,
  FLASH_DURATION_MS,
  RESOLUTION_DURATION_MS,
  REDUCED_DURATION_MS,
  WINNING_PULSE_PEAK_SCALE,
  flashProgress,
  PILE_STACK_OFFSET,
  FLASH_COLORS,
  easeOut,
  trickPosForSeat,
  nameplateCenter,
  pilePosForSeat,
  cardSize,
  slideFromCenter,
  clearOfTableMarks,
};
